import { protocolHandler } from "../handlers/protocol-handler";
import {
  orpcIntegration,
  type ORPCIntegrationCallbacks,
} from "../integration/orpc-integration";
import type {
  ChatMessage,
  ChatConversation,
  ChatThread,
  TagReference,
} from "@pineseed/gui/types/chat";
import type {
  messageStructureTypes,
  Uuid,
  ParticipantId,
} from "@pineseed/spec";

// Use spec types for protocol handler calls (branded types)
type SpecSendMessageRequest = messageStructureTypes["SendMessageRequest"];
type SpecCreateConversationRequest =
  messageStructureTypes["CreateConversationRequest"];
type SpecCreateThreadRequest = messageStructureTypes["CreateThreadRequest"];
type ChatCacheResponse = messageStructureTypes["ChatCacheResponse"];
import type { Tag } from "@pineseed/gui/types";
import { DEFAULT_WORKSPACE_ID } from "../config/workspace";
import {
  getAuthTokens,
  createSecureStorage,
} from "@pineseed/gui/util/secure-storage";
import type { EnvConfig } from "../config/env";

/**
 * Chat Handler - 100% oRPC Based
 * Handles all chat functionality using the oRPC handler with generated API clients
 * STRICT RULE: Business logic only, no UI code, no direct state mutations
 */

export interface QueuedMessage {
  id: string;
  conversationId: string;
  threadId?: string;
  content: string;
  participantId: string; // Device fingerprint is required
  timestamp: Date;
  retryCount: number;
}

export interface ChatHandlerCallbacks {
  onMessageStart: (conversationId: string, messageId: string) => void;
  onMessageComplete: (
    conversationId: string,
    messageId: string,
    content: string,
    role: "user" | "assistant",
    reasoning?: string,
    references?: TagReference[],
  ) => void;
  onSSEMessageReceived: (message: ChatMessage) => void;
  onMessageError: (conversationId: string, error: string) => void;
  onSetLoading: (loading: boolean) => void;
  onSetError: (error: string | null) => void;
  onAddPendingResponse: (responseId: string) => void;
  onRemovePendingResponse: (responseId: string) => void;
  onIncrementPending: (conversationId: string) => void;
  onDecrementPending: (conversationId: string) => void;
  onUpdateMessageStatus: (
    responseId: string,
    conversationId: string,
    messageId: string,
    status: string,
  ) => void;
  // Tag system integration
  getActiveWorkspaceTags: () => Tag[];
  findOrCreateTag: (name: string, type?: Tag["type"]) => Tag;
  // Conversation and thread management
  onConversationCreated: (conversation: ChatConversation) => void;
  onThreadCreated: (thread: ChatThread) => void;
  onConversationsLoaded: (conversations: ChatConversation[]) => void;
  onThreadsLoaded: (threads: ChatThread[]) => void;
  onMessagesLoaded: (conversationId: string, messages: ChatMessage[]) => void;
  // Queueing callbacks
  onAddQueuedMessage?: (
    message: Omit<QueuedMessage, "id" | "timestamp" | "retryCount">,
  ) => string;
  onRemoveQueuedMessage?: (messageId: string) => void;
  onClearQueuedMessages?: () => void;
  onGetQueuedMessagesForConversation?: (
    conversationId: string,
  ) => QueuedMessage[];
  onGetAllQueuedMessages?: () => QueuedMessage[];
}

export interface ChatSettings {
  defaultModel: string;
  maxTokens: number;
  temperature: number;
  systemPrompt: string;
  autoSave: boolean;
  enableReasoning: boolean;
  asyncMode: boolean;
}

export class ChatHandler {
  private static instance: ChatHandler;
  private callbacks?: ChatHandlerCallbacks;
  private pendingResponses = new Map<string, AbortController>();
  private orpcEnabled = false;
  private currentWorkspaceId = DEFAULT_WORKSPACE_ID;
  private conversationThreadMapping = new Map<string, string>(); // conversationId -> threadId
  private loadedThreadsForConversations = new Set<string>(); // Track which conversations have had threads loaded
  // Removed: currentAuthToken - now retrieved from secure storage when needed
  private isInitialized = false; // Track initialization state
  private isInitializing = false; // Track if currently initializing
  private orpcCallbacks?: ORPCIntegrationCallbacks; // Store callbacks for cleanup
  private envConfig?: EnvConfig; // Store config passed during initialization
  private secureStorage = createSecureStorage(); // Secure storage for auth tokens

  // Queue processing state (queue data is in persisted state)
  private isProcessingQueue = false;

  private constructor() {
    // 🚨 CRITICAL: DO NOT initialize oRPC in constructor
    // oRPC initialization will happen in initializeWithContext() when we have a JWT token
    console.log(
      "🎯 ChatHandler created - oRPC initialization deferred until JWT token is available",
    );
  }

  public static getInstance(): ChatHandler {
    if (!ChatHandler.instance) {
      ChatHandler.instance = new ChatHandler();
    }
    return ChatHandler.instance;
  }

  public setCallbacks(callbacks: ChatHandlerCallbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Initialize protocol integration for real-time messaging
   */
  private async initializeORPCIntegration() {
    // Check if protocol communication is enabled (defaults to false unless explicitly enabled)
    const protocolEnabled = this.envConfig?.protocol.orpcEnabled === true;
    if (!protocolEnabled) {
      console.log("🔌 Protocol integration disabled by environment config");
      this.orpcEnabled = false;
      return;
    }

    console.log(
      "🔌 Chat handler deferring to protocol integration for connection management...",
    );

    // 🚨 CRITICAL: Chat handler should NOT initialize its own protocol connection
    // This is handled by protocol integration layer in initializeWithContext()

    this.orpcEnabled = true; // Will be updated by connection status callbacks
  }

  /**
   * Handle incoming oRPC message from real-time subscription
   */
  private handleORPCMessage(message: ChatMessage) {
    // Convert timestamp to Date if it's a string (from SSE)
    const uiMessage: ChatMessage = {
      ...message,
      timestamp:
        typeof message.timestamp === "string"
          ? new Date(message.timestamp)
          : message.timestamp,
    };

    // SSE messages are complete and should be added directly with server-provided participantId
    this.callbacks?.onSSEMessageReceived(uiMessage);
  }

  /**
   * Send a message using oRPC
   */
  public async sendMessageAsync(
    conversationId: string,
    content: string,
    settings: ChatSettings,
    _messages: ChatMessage[],
    participantId: string,
  ): Promise<string> {
    console.log("📝 ChatHandler.sendMessageAsync:", {
      orpcEnabled: this.orpcEnabled,
      conversationId,
      workspaceId: this.currentWorkspaceId,
      hasThreadMapping: this.conversationThreadMapping.has(conversationId),
    });

    if (!this.orpcEnabled) {
      console.log("🔄 oRPC not enabled, attempting re-initialization...");
      await this.reinitializeORP();

      if (!this.orpcEnabled) {
        throw new Error("oRPC service is not available");
      }
    }

    try {
      // 🚨 CRITICAL: Ensure oRPC handler has JWT token before making calls
      const { accessToken } = await getAuthTokens(this.secureStorage);
      if (!accessToken) {
        console.warn("⚠️ No JWT token available for sending message");
        throw new Error("JWT token required for sending messages");
      }
      // Token is already set during initialization - no need to set it again

      // Ensure we have a thread for this conversation
      const threadId = await this.ensureThreadForConversation(conversationId);

      // Create the user message first
      const userMessageId = crypto.randomUUID();
      // const userMessage: ChatMessage = {
      //   id: userMessageId,
      //   content,
      //   role: "user",
      //   timestamp: new Date(),
      //   threadId,
      //   conversationId,
      //   workspaceId: this.currentWorkspaceId,
      //   isStreaming: false,
      //   status: "sending",
      // };

      // User message will be added when it comes back from server via SSE

      // Create the message request
      const messageRequest: SpecSendMessageRequest = {
        content,
        role: "user",
        threadId: threadId as Uuid,
        conversationId: conversationId as Uuid,
        workspaceId: this.currentWorkspaceId as Uuid,
        participantId: participantId as ParticipantId, // Client-generated device fingerprint hash (required)
        metadata: {
          model: settings.defaultModel,
          tokensUsed: 0, // Will be filled by server
          responseTime: 0, // Will be filled by server
        },
      };

      // Send the message and expect assistant response via SSE
      const responseId = crypto.randomUUID();
      this.callbacks?.onAddPendingResponse(responseId);
      this.callbacks?.onIncrementPending(conversationId);

      try {
        // Update user message status to sent
        this.callbacks?.onUpdateMessageStatus(
          responseId,
          conversationId,
          userMessageId,
          "pending",
        );

        console.log("📨 Sending message to server:", messageRequest);

        // Send message to server - response will come via SSE
        const message = await protocolHandler.sendMessage(messageRequest);

        console.log("📨 Message sent to server:", message);

        // Update user message status to sent
        this.callbacks?.onUpdateMessageStatus(
          responseId,
          conversationId,
          message.id,
          message.status,
        );

        return userMessageId;
      } catch (error) {
        // Update user message status to failed
        this.callbacks?.onUpdateMessageStatus(
          responseId,
          conversationId,
          userMessageId,
          "failed",
        );
        throw error;
      } finally {
        this.callbacks?.onRemovePendingResponse(responseId);
        this.callbacks?.onDecrementPending(conversationId);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to send message";
      console.error("Failed to send message via oRPC:", error);
      this.callbacks?.onMessageError(conversationId, errorMessage);
      throw error;
    }
  }

  /**
   * Ensure we have a thread for the given conversation
   */
  private async ensureThreadForConversation(
    conversationId: string,
  ): Promise<string> {
    // Check if we already have a thread mapping
    if (this.conversationThreadMapping.has(conversationId)) {
      return this.conversationThreadMapping.get(conversationId)!;
    }

    // Import workspace config to check for default conversation ID
    const { workspaceConfig } = await import("../config/workspace");
    const workspaceConfigInstance = workspaceConfig(this.envConfig!);

    // For default conversation, use default thread ID from config
    if (conversationId === workspaceConfigInstance.getDefaultConversationId()) {
      console.log("🧵 Using default thread ID for default conversation");
      const defaultThreadId = workspaceConfigInstance.getDefaultThreadId();
      this.conversationThreadMapping.set(conversationId, defaultThreadId);
      return defaultThreadId;
    }

    try {
      // 🚨 CRITICAL: Ensure oRPC handler has JWT token before making calls
      const { accessToken } = await getAuthTokens(this.secureStorage);
      if (!accessToken) {
        console.warn("⚠️ No JWT token available for thread creation");
        throw new Error("JWT token required for creating threads");
      }
      // Token is already set during initialization - no need to set it again

      // Create a new thread for this conversation
      const threadRequest: SpecCreateThreadRequest = {
        conversationId: conversationId as Uuid,
        title: `Thread for conversation ${conversationId}`,
      };

      const thread = await protocolHandler.createThread(threadRequest);
      this.conversationThreadMapping.set(conversationId, thread.id);

      return thread.id;
    } catch (error) {
      console.error("Failed to create thread for conversation:", error);
      throw new Error("Failed to create thread for conversation");
    }
  }

  /**
   * Create a new conversation
   */
  public async createConversation(
    title: string,
    systemPrompt?: string,
  ): Promise<ChatConversation> {
    if (!this.orpcEnabled) {
      await this.reinitializeORP();
      if (!this.orpcEnabled) {
        throw new Error("oRPC service is not available");
      }
    }

    try {
      // 🚨 CRITICAL: Ensure oRPC handler has JWT token before making calls
      const { accessToken } = await getAuthTokens(this.secureStorage);
      if (!accessToken) {
        console.warn("⚠️ No JWT token available for conversation creation");
        throw new Error("JWT token required for creating conversations");
      }
      // Token is already set during initialization - no need to set it again

      const request: SpecCreateConversationRequest = {
        title,
        workspaceId: this.currentWorkspaceId as Uuid,
        systemPrompt,
      };

      const conversation = await protocolHandler.createConversation(request);

      // Convert timestamp strings to Date for UI compatibility
      const uiConversation: ChatConversation = {
        ...conversation,
        createdAt: new Date(conversation.createdAt),
        updatedAt: new Date(conversation.updatedAt),
      };

      return uiConversation;
    } catch (error) {
      console.error("Failed to create conversation:", error);
      throw error;
    }
  }

  /**
   * Load conversations for the current workspace
   */
  public async loadConversations(limit: number = 20): Promise<void> {
    if (!this.orpcEnabled) {
      await this.reinitializeORP();
      if (!this.orpcEnabled) {
        throw new Error("oRPC service is not available");
      }
    }

    try {
      console.log(
        "📂 Loading conversations for workspace:",
        this.currentWorkspaceId,
      );

      // 🚨 CRITICAL: Ensure oRPC handler has JWT token before making calls
      const { accessToken } = await getAuthTokens(this.secureStorage);
      if (!accessToken) {
        console.warn("⚠️ No JWT token available for loading conversations");
        throw new Error("JWT token required for loading conversations");
      }
      // Token is already set during initialization - no need to set it again

      const response = await protocolHandler.getConversations({
        workspaceId: this.currentWorkspaceId,
        limit,
      });

      // Convert API conversations to UI format
      const conversations: ChatConversation[] = response.conversations.map(
        (conv) => ({
          ...conv,
          createdAt: new Date(conv.createdAt),
          updatedAt: new Date(conv.updatedAt),
          messages: [], // Messages will be loaded separately
        }),
      );

      console.log("📂 Loaded conversations:", conversations.length);

      // Trigger callback to update state
      this.callbacks?.onConversationsLoaded?.(conversations);
    } catch (error) {
      console.error("Failed to load conversations:", error);
      throw error;
    }
  }

  /**
   * Load chat cache for the current workspace (conversations and cached messages)
   */
  public async loadChatCache(): Promise<void> {
    if (!this.orpcEnabled) {
      await this.reinitializeORP();
      if (!this.orpcEnabled) {
        throw new Error("oRPC service is not available");
      }
    }

    try {
      console.log(
        "💾 Loading chat cache for workspace:",
        this.currentWorkspaceId,
      );

      // 🚨 CRITICAL: Ensure oRPC handler has JWT token before making calls
      const { accessToken } = await getAuthTokens(this.secureStorage);
      if (!accessToken) {
        console.warn("⚠️ No JWT token available for loading chat cache");
        throw new Error("JWT token required for loading chat cache");
      }

      const cacheResponse = await protocolHandler.getChatCache({
        workspaceId: this.currentWorkspaceId,
      });

      console.log("💾 Loaded chat cache:", {
        workspaceId: cacheResponse.workspaceId,
        conversationCount: Object.keys(cacheResponse.conversations).length,
        updatedAt: cacheResponse.updatedAt,
      });

      // Process cache response and load conversations with their messages
      await this.processChatCache(cacheResponse);
    } catch (error) {
      console.error("Failed to load chat cache:", error);
      // Don't throw error to prevent blocking initialization if cache fails
      console.log("⚠️ Continuing initialization without cache");
    }
  }

  /**
   * Process chat cache response and load conversations with messages
   */
  private async processChatCache(
    cacheResponse: ChatCacheResponse,
  ): Promise<void> {
    try {
      // First, load all conversations for the workspace
      await this.loadConversations();

      // Then load messages for each conversation that has cached data
      const conversationIds = Object.keys(cacheResponse.conversations);

      console.log(
        `💾 Processing cached messages for ${conversationIds.length} conversations`,
      );

      // Load messages for each conversation in parallel (but limit concurrency)
      const loadPromises = conversationIds.map(async (conversationId) => {
        try {
          await this.loadMessages(conversationId);
          console.log(
            `✅ Loaded cached messages for conversation: ${conversationId}`,
          );
        } catch (error) {
          console.warn(
            `⚠️ Failed to load messages for conversation ${conversationId}:`,
            error,
          );
        }
      });

      // Wait for all message loading to complete
      await Promise.allSettled(loadPromises);

      console.log("✅ Chat cache processing completed");
    } catch (error) {
      console.error("Failed to process chat cache:", error);
    }
  }

  /**
   * Load threads for a specific conversation
   */
  public async loadThreads(conversationId: string): Promise<void> {
    if (!this.orpcEnabled) {
      await this.reinitializeORP();
      if (!this.orpcEnabled) {
        throw new Error("oRPC service is not available");
      }
    }

    try {
      console.log("🧵 Loading threads for conversation:", conversationId);

      // IDEMPOTENT: Check if threads for this conversation have already been loaded
      if (this.loadedThreadsForConversations.has(conversationId)) {
        console.log(
          "🧵 Threads already loaded for conversation:",
          conversationId,
        );
        return; // Skip loading to prevent duplicates
      }

      // For now, we create/ensure the default thread exists
      // In future, this could call an API endpoint to get all threads for a conversation
      const threadId = await this.ensureThreadForConversation(conversationId);

      // Create a thread object for the UI
      const thread: ChatThread = {
        id: threadId,
        conversationId,
        title: "Main Thread", // Default thread title
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true,
      };

      console.log("🧵 Loaded thread:", thread.id);

      // Mark this conversation as having threads loaded
      this.loadedThreadsForConversations.add(conversationId);

      // Trigger callback to update state
      this.callbacks?.onThreadsLoaded?.([thread]);
    } catch (error) {
      console.error("Failed to load threads:", error);
      throw error;
    }
  }

  /**
   * Load messages for a specific conversation
   */
  public async loadMessages(
    conversationId: string,
    limit: number = 50,
  ): Promise<void> {
    if (!this.orpcEnabled) {
      await this.reinitializeORP();
      if (!this.orpcEnabled) {
        throw new Error("oRPC service is not available");
      }
    }

    try {
      console.log("💬 Loading messages for conversation:", conversationId);

      // 🚨 CRITICAL: Ensure oRPC handler has JWT token before making calls
      const { accessToken } = await getAuthTokens(this.secureStorage);
      if (!accessToken) {
        console.warn("⚠️ No JWT token available for loading messages");
        throw new Error("JWT token required for loading messages");
      }
      // Token is already set during initialization - no need to set it again

      // 🚨 CRITICAL: Get threadId for the conversation - API requires threadId, not conversationId
      const threadId = await this.ensureThreadForConversation(conversationId);

      const response = await protocolHandler.getMessages({
        workspaceId: this.currentWorkspaceId,
        conversationId,
        threadId,
        limit,
      });

      // Convert API messages to UI format
      const messages: ChatMessage[] = response.messages.map((msg) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));

      console.log("💬 Loaded messages:", messages.length);

      // Trigger callback to update state
      this.callbacks?.onMessagesLoaded?.(conversationId, messages);
    } catch (error) {
      console.error("Failed to load messages:", error);
      throw error;
    }
  }

  /**
   * Cancel a response
   */
  public cancelResponse(responseId: string): void {
    const controller = this.pendingResponses.get(responseId);
    if (controller) {
      controller.abort();
      this.pendingResponses.delete(responseId);
      this.callbacks?.onRemovePendingResponse(responseId);
    }
  }

  /**
   * Cancel all pending responses
   */
  public cancelAllResponses(): void {
    for (const [responseId, controller] of this.pendingResponses.entries()) {
      controller.abort();
      this.callbacks?.onRemovePendingResponse(responseId);
    }
    this.pendingResponses.clear();
  }

  /**
   * Generate conversation title
   */
  public generateConversationTitle(firstMessage: string): string {
    const words = firstMessage.split(" ").slice(0, 6);
    return words.join(" ") + (firstMessage.split(" ").length > 6 ? "..." : "");
  }

  /**
   * Validate message content
   */
  public validateMessage(content: string): { valid: boolean; error?: string } {
    if (!content.trim()) {
      return { valid: false, error: "Message cannot be empty" };
    }
    if (content.length > 10000) {
      return {
        valid: false,
        error: "Message is too long (max 10,000 characters)",
      };
    }
    return { valid: true };
  }

  /**
   * Check if oRPC is enabled
   */
  public isORPCEnabled(): boolean {
    return this.orpcEnabled;
  }

  /**
   * Reinitialize oRPC connection
   */
  public async reinitializeORP(): Promise<void> {
    console.log("🔄 Reinitializing oRPC integration...");
    await this.initializeORPCIntegration();
  }

  /**
   * Get oRPC status
   */
  public getORPCStatus(): {
    enabled: boolean;
    workspaceId: string;
    conversationCount: number;
    threadCount: number;
  } {
    return {
      enabled: this.orpcEnabled,
      workspaceId: this.currentWorkspaceId,
      conversationCount: 0, // TODO: Track this
      threadCount: this.conversationThreadMapping.size,
    };
  }

  /**
   * Cleanup resources
   */
  public cleanup(): void {
    this.cancelAllResponses();
    this.conversationThreadMapping.clear();
    this.loadedThreadsForConversations.clear(); // Clear loaded threads tracking

    // 🚨 CRITICAL: Unregister callbacks from oRPC integration to prevent memory leaks
    if (this.orpcCallbacks) {
      orpcIntegration.unregisterCallbacks(
        this.currentWorkspaceId,
        this.orpcCallbacks,
      );
      this.orpcCallbacks = undefined;
    }

    // Auth token now retrieved from secure storage when needed
    this.isInitialized = false; // Reset initialization state
    this.isInitializing = false; // Reset initializing state
    protocolHandler.cleanup();
    this.callbacks = undefined;
  }

  /**
   * Reset initialization state (for testing or forced re-initialization)
   */
  public resetInitialization(): void {
    this.isInitialized = false;
    this.isInitializing = false;
    this.orpcEnabled = false;
  }

  /**
   * Set current workspace ID
   */
  public setWorkspaceId(workspaceId: string): void {
    this.currentWorkspaceId = workspaceId;
  }

  /**
   * Initialize handler with context from state
   * Following architecture: State → Handler (via method calls with context)
   */
  public async initializeWithContext(context: {
    workspaceId: string | null;
    accessToken: string | null;
    envConfig: EnvConfig;
  }): Promise<void> {
    // 🚨 CRITICAL: Prevent concurrent initialization attempts
    if (this.isInitializing) {
      console.log(
        "🔄 ChatHandler already initializing - skipping duplicate attempt",
      );
      return;
    }

    // 🚨 CRITICAL: Check if already initialized with same context
    if (
      this.isInitialized &&
      this.currentWorkspaceId ===
        (context.workspaceId || DEFAULT_WORKSPACE_ID) &&
      true // Context comparison removed - always reinitialize for security
    ) {
      console.log(
        "✅ ChatHandler already initialized with same context - skipping",
      );
      return;
    }

    this.isInitializing = true;

    try {
      console.log("🚀 ChatHandler initializing with context:", {
        workspaceId: context.workspaceId,
        hasToken: !!context.accessToken,
      });

      // Store context and config
      this.currentWorkspaceId = context.workspaceId || DEFAULT_WORKSPACE_ID;
      this.envConfig = context.envConfig;
      // Auth token now retrieved from secure storage when needed

      // Initialize oRPC integration (architectural single point)
      await this.initializeORPCIntegration();

      // 🚨 CRITICAL: Initialize orpcIntegration with callbacks (SINGLE POINT OF TRUTH)
      if (this.orpcEnabled && context.accessToken) {
        console.log(
          "🔌 Registering chat handler callbacks with orpcIntegration...",
        );

        // Create callbacks for orpcIntegration
        this.orpcCallbacks = {
          onMessage: (message) => {
            this.handleORPCMessage(message);
          },
          onConnectionStatusChange: (connected) => {
            console.log("🔌 oRPC connection status changed:", connected);
            this.orpcEnabled = connected;

            // 🚨 IMPORTANT: App state refresh on reconnection is handled centrally by ORPCConnectionBridge
            // Do NOT add queue processing or other refresh logic here - it belongs in refreshAppStateOnReconnection()
            // if (connected) {
            //   console.log(
            //     "✅ Connection restored - app refresh handled by connection bridge",
            //   );
            // }
          },
          // onConnectionStatusUpdate: (status) => {
          //   console.log("🔌 oRPC detailed connection status update:", status);
          //   // Handler should not update state atoms directly - this violates architecture
          //   // State updates should happen via callback pattern to components/hooks
          // },
          onError: (error) => {
            console.error("❌ oRPC integration error:", JSON.stringify(error));
            this.callbacks?.onSetError(
              JSON.parse(error.message).error || "Unknown error",
            );
          },
        };

        // Register callbacks with the SINGLE orpcIntegration
        await orpcIntegration.initialize(
          this.currentWorkspaceId,
          this.orpcCallbacks,
          context.accessToken,
        );

        console.log(
          "✅ Chat handler registered with orpcIntegration successfully",
        );

        // Load chat cache (conversations and messages) for the workspace
        await this.loadChatCache();
      }

      this.isInitialized = true;
      console.log("🏁 ChatHandler initialization complete");
    } catch (error) {
      console.error("❌ ChatHandler initialization failed:", error);
      this.callbacks?.onSetError(
        `Failed to initialize chat: ${error instanceof Error ? error.message : String(error)}`,
      );
      throw error;
    } finally {
      this.isInitializing = false;
    }
  }

  // ===== MESSAGE QUEUE MANAGEMENT =====

  /**
   * Add message to queue when oRPC connection is down
   */
  public queueMessage(
    conversationId: string,
    content: string,
    participantId: string,
    threadId?: string,
  ): string {
    if (!this.callbacks?.onAddQueuedMessage) {
      throw new Error("Queue callback not available");
    }

    const messageId = this.callbacks.onAddQueuedMessage({
      conversationId,
      threadId,
      content,
      participantId,
    });

    console.log(`📥 Message queued: ${messageId}`);
    return messageId;
  }

  /**
   * Remove message from queue
   */
  public removeQueuedMessage(messageId: string): void {
    if (this.callbacks?.onRemoveQueuedMessage) {
      this.callbacks.onRemoveQueuedMessage(messageId);
      console.log(`📤 Message removed from queue: ${messageId}`);
    }
  }

  /**
   * Get queued messages for a specific conversation
   */
  public getQueuedMessagesForConversation(
    conversationId: string,
  ): QueuedMessage[] {
    return (
      this.callbacks?.onGetQueuedMessagesForConversation?.(conversationId) || []
    );
  }

  /**
   * Clear all queued messages
   */
  public clearMessageQueue(): void {
    if (this.callbacks?.onClearQueuedMessages) {
      this.callbacks.onClearQueuedMessages();
      console.log("🗑️ Message queue cleared");
    }
  }

  /**
   * Convert a temporary conversation to a real one when connection is restored
   */
  public async convertTemporaryConversation(
    temporaryConversationId: string,
    title: string,
    systemPrompt?: string,
  ): Promise<string | null> {
    if (!this.orpcEnabled) {
      console.log(
        "🔄 oRPC not enabled - cannot convert temporary conversation",
      );
      return null;
    }

    try {
      console.log(
        `🔄 Converting temporary conversation: ${temporaryConversationId}`,
      );

      // Create a real conversation
      const realConversation = await this.createConversation(
        title,
        systemPrompt,
      );

      console.log(
        `✅ Temporary conversation converted: ${temporaryConversationId} → ${realConversation.id}`,
      );

      return realConversation.id;
    } catch (error) {
      console.error(
        `❌ Failed to convert temporary conversation ${temporaryConversationId}:`,
        error,
      );
      return null;
    }
  }

  /**
   * Process queued messages when connection is restored
   */
  public async processQueuedMessages(): Promise<void> {
    if (this.isProcessingQueue) {
      return;
    }

    if (!this.orpcEnabled) {
      console.log("🔄 oRPC not enabled - cannot process queued messages");
      return;
    }

    if (!this.callbacks?.onGetQueuedMessagesForConversation) {
      console.log("📥 No queue callback available");
      return;
    }

    this.isProcessingQueue = true;

    try {
      // Get ALL queued messages directly from persisted state
      const allQueuedMessages =
        this.callbacks?.onGetAllQueuedMessages?.() || [];

      // console.log(
      //   `📊 Debug: Found ${allQueuedMessages.length} queued messages total`,
      // );

      if (allQueuedMessages.length === 0) {
        console.log("📥 No queued messages found");
        return;
      }

      console.log(`📤 Processing ${allQueuedMessages.length} queued messages`);

      // Group messages by conversation
      const messagesByConversation = new Map<string, QueuedMessage[]>();
      for (const message of allQueuedMessages) {
        if (!messagesByConversation.has(message.conversationId)) {
          messagesByConversation.set(message.conversationId, []);
        }
        messagesByConversation.get(message.conversationId)!.push(message);
      }

      console.log(
        `📤 Processing messages for ${messagesByConversation.size} conversations`,
      );

      // Process each conversation's messages
      for (const [conversationId, messages] of messagesByConversation) {
        try {
          for (const queuedMessage of messages) {
            try {
              console.log(`📤 Processing queued message: ${queuedMessage.id}`);

              // Use the same sendMessageAsync path as normal messages for consistency
              await this.sendMessageAsync(
                conversationId,
                queuedMessage.content,
                {
                  defaultModel: "default", // Use default model for queued messages
                  maxTokens: 4000,
                  temperature: 0.7,
                  systemPrompt: "You are a helpful AI assistant.",
                  autoSave: true,
                  enableReasoning: true,
                  asyncMode: true,
                },
                [], // Empty messages array - handler will get current messages
                queuedMessage.participantId, // Use stored participantId from when message was queued
              );

              // Remove from queue on success
              this.removeQueuedMessage(queuedMessage.id);

              console.log(
                `✅ Queued message sent successfully: ${queuedMessage.id}`,
              );
            } catch (error) {
              console.error(
                `❌ Failed to send queued message ${queuedMessage.id}:`,
                error,
              );

              // For retry logic, we'd need to update the message in the persisted state
              // For now, just remove messages that fail too many times
              if (queuedMessage.retryCount >= 2) {
                console.warn(
                  `🚫 Max retries exceeded for message ${queuedMessage.id} - removing from queue`,
                );
                this.removeQueuedMessage(queuedMessage.id);
                this.callbacks?.onMessageError?.(
                  queuedMessage.conversationId,
                  `Failed to send queued message after ${queuedMessage.retryCount + 1} attempts`,
                );
              }
            }
          }
        } catch (error) {
          console.error(
            `❌ Failed to process messages for conversation ${conversationId}:`,
            error,
          );
        }
      }

      console.log(`✅ Queue processing completed`);
    } finally {
      this.isProcessingQueue = false;
    }
  }

  /**
   * Sync temporary conversations when connection is restored
   */
  public async syncTemporaryConversations(): Promise<void> {
    if (!this.orpcEnabled || !this.callbacks) {
      return;
    }

    // console.log("🔄 Syncing temporary conversations with server");

    try {
      // Get temporary conversations from state via callback
      // Note: This would need to be implemented as a callback to get state data
      // For now, we'll rely on the queue processing to handle the messages
      console.log(
        "📝 Temporary conversation sync - relying on message queue processing",
      );
    } catch (error) {
      console.error("❌ Failed to sync temporary conversations:", error);
    }
  }
}

// Export singleton instance
export const chatHandler = ChatHandler; //.getInstance();
